import {
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  RobotOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Input, message, Modal, Popconfirm, Spin, Switch, Tag, Tooltip, Typography } from "antd";
import React, { useEffect, useRef, useState } from "react";
import {
  closeSession,
  createSession,
  deleteMyKylasLead,
  getSession,
  listSessions,
  sendMessage,
} from "../../apis/design_agent.api";
import { getImageUrl } from "../../helpers/image_helper";
import styles from "./index.module.scss";

const { Text } = Typography;
const { TextArea } = Input;

interface Product {
  gst: number;
  mrp: number;
  size: string;
  brand: string;
  color: string;
  finish: string[];
  category: string;
  in_store: boolean;
  material: string[];
  image_url: string;
  price_unit: string;
  variant_id: number;
  variant_handle: string;
  application: string[];
  package_size: number | null;
  product_name: string;
  coverage_area: number;
  price_per_unit: number;
  alternate_price: number;
}

interface AgentMessage {
  id: number;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  created_at: string;
  tool_name?: string | null;
  tool_result?: any;
  products?: Product[];
}

interface ContactInfo {
  name: string;
  phone: string;
}

type SessionMode = "personal" | "sales" | "admin";

interface AgentSession {
  id: number;
  title: string;
  room_context: any;
  status: string;
  mode?: SessionMode;
  contact_info?: ContactInfo | null;
  created_at: string;
  modified_at: string;
  messages: AgentMessage[];
  message_count: number;
}

// --- Markdown-like formatter ---

const formatContent = (text: string) => {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];

  lines.forEach((line, i) => {
    let node: React.ReactNode;

    if (line.startsWith("### ")) {
      node = <h4 key={i} className={styles.mdH4}>{parseInline(line.slice(4))}</h4>;
    } else if (line.startsWith("## ")) {
      node = <h3 key={i} className={styles.mdH3}>{parseInline(line.slice(3))}</h3>;
    } else if (line.startsWith("# ")) {
      node = <h2 key={i} className={styles.mdH2}>{parseInline(line.slice(2))}</h2>;
    } else if (/^\d+\.\s/.test(line)) {
      const match = line.match(/^(\d+\.)\s(.*)/);
      node = (
        <div key={i} className={styles.mdListItem}>
          <span className={styles.mdListNum}>{match?.[1]}</span>
          <span>{parseInline(match?.[2] || "")}</span>
        </div>
      );
    } else if (/^[-*]\s/.test(line.trim())) {
      const indent = line.search(/\S/);
      node = (
        <div key={i} className={styles.mdBullet} style={{ paddingLeft: indent > 0 ? indent * 6 : 0 }}>
          <span className={styles.mdBulletDot} />
          <span>{parseInline(line.trim().slice(2))}</span>
        </div>
      );
    } else if (/^!\[.*?\]\(.*?\)$/.test(line.trim())) {
      const imgMatch = line.trim().match(/^!\[(.*?)\]\((.*?)\)$/);
      if (imgMatch) {
        node = (
          <img
            key={i}
            src={getImageUrl(imgMatch[2], "100")}
            alt={imgMatch[1]}
            className={styles.mdProductImg}
          />
        );
      }
    } else if (IMAGE_URL_RE.test(line.trim()) && line.trim().match(IMAGE_URL_RE)?.[0] === line.trim()) {
      node = (
        <img
          key={i}
          src={getImageUrl(line.trim(), "100")}
          alt=""
          className={styles.mdProductImg}
        />
      );
    } else if (/^---+$/.test(line.trim())) {
      node = <hr key={i} className={styles.mdHr} />;
    } else if (line.trim() === "") {
      node = <div key={i} className={styles.mdSpacer} />;
    } else {
      node = <p key={i} className={styles.mdParagraph}>{parseInline(line)}</p>;
    }

    elements.push(node);
  });

  return elements;
};

const IMAGE_URL_RE = /https?:\/\/\S+\.(?:png|jpe?g|gif|webp|svg|bmp)(?:\?\S*)?/i;

const parseInline = (text: string): React.ReactNode => {
  // Handle markdown images, bold, and plain image URLs
  const parts = text.split(
    /(!\[.*?\]\(.*?\)|\*\*[^*]+\*\*|https?:\/\/\S+\.(?:png|jpe?g|gif|webp|svg|bmp)(?:\?\S*)?)/gi
  );
  return parts.map((part, i) => {
    const imgMatch = part.match(/^!\[(.*?)\]\((.*?)\)$/);
    if (imgMatch) {
      return (
        <img
          key={i}
          src={getImageUrl(imgMatch[2], "100")}
          alt={imgMatch[1]}
          className={styles.mdProductImg}
        />
      );
    }
    if (IMAGE_URL_RE.test(part)) {
      return (
        <img
          key={i}
          src={getImageUrl(part, "100")}
          alt=""
          className={styles.mdProductImg}
        />
      );
    }
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// --- Suggestion cards ---

const SUGGESTIONS = [
  {
    title: "Bedroom Tiles",
    text: "I need tiles for my bedroom, 120 sqft, budget 50k, beige color",
    icon: "🛏️",
  },
  {
    title: "Bathroom Tiles",
    text: "Suggest wall tiles for a 120 sqft bathroom under 30k",
    icon: "🚿",
  },
  {
    title: "Kitchen Laminates",
    text: "I want laminates for kitchen cabinets, white matte finish",
    icon: "🍳",
  },
  {
    title: "Living Room Makeover",
    text: "Help me design my 200 sqft living room, budget 1.5 lakh, modern style",
    icon: "🛋️",
  },
];

// --- Main Component ---

const DesignAgent = () => {
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [activeSession, setActiveSession] = useState<AgentSession | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [creatingSession, setCreatingSession] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [simulateWhatsapp, setSimulateWhatsapp] = useState(false);
  const [forceNewLead, setForceNewLead] = useState(false);
  const [deletingLead, setDeletingLead] = useState(false);

  const handleDeleteKylasLead = async () => {
    setDeletingLead(true);
    const res = await deleteMyKylasLead();
    setDeletingLead(false);
    if (res?.status === "deleted") {
      message.success(`Kylas lead deleted (id ${res.lead_id})`);
    } else if (res?.status === "noop") {
      message.info(res.message || "No Kylas lead found for your contact");
    }
  };
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [activeSession?.messages, sendingMessage]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const loadSessions = async () => {
    setLoading(true);
    const data = await listSessions();
    if (data) {
      setSessions(data);
    }
    setLoading(false);
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const handleConfirmCreateSession = async () => {
    setCreatingSession(true);
    const data = await createSession();
    if (data) {
      setSessions((prev) => [data, ...prev]);
      setActiveSession(data);
      setShowCreateModal(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    setCreatingSession(false);
  };

  const handleSelectSession = async (sessionId: number) => {
    if (activeSession?.id === sessionId) return;
    setLoading(true);
    const data = await getSession(sessionId);
    if (data) {
      setActiveSession(data);
    }
    setLoading(false);
  };

  const handleSendMessage = async (overrideText?: string) => {
    const text = overrideText || inputValue;
    if (!text.trim() || !activeSession || sendingMessage) return;

    const userMessage = text.trim();
    setInputValue("");

    const tempUserMsg: AgentMessage = {
      id: Date.now(),
      role: "user",
      content: userMessage,
      created_at: new Date().toISOString(),
    };
    setActiveSession((prev) =>
      prev ? { ...prev, messages: [...(prev.messages || []), tempUserMsg] } : prev
    );

    setSendingMessage(true);
    const data = await sendMessage(
      activeSession.id,
      userMessage,
      simulateWhatsapp,
      simulateWhatsapp && forceNewLead
    );
    if (data) {
      setActiveSession(data.session);
      setSessions((prev) =>
        prev.map((s) => (s.id === data.session.id ? data.session : s))
      );
    }
    setSendingMessage(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleCloseSession = async (sessionId: number) => {
    const data = await closeSession(sessionId);
    if (data) {
      message.success("Session closed");
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, status: "closed" } : s))
      );
      if (activeSession?.id === sessionId) {
        setActiveSession((prev) => (prev ? { ...prev, status: "closed" } : prev));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  };

  const hasMessages = activeSession?.messages && activeSession.messages.length > 0;
  const isClosed = activeSession?.status === "closed";

  return (
    <div className={styles.container}>
      {/* ---- Sidebar ---- */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarTop}>
          <span className={styles.sidebarLogo}>
            <RobotOutlined /> Design Agent
          </span>
          <Tooltip title="New chat">
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              loading={creatingSession}
              className={styles.newChatBtn}
            />
          </Tooltip>
        </div>

        <div className={styles.sessionList}>
          {loading && sessions.length === 0 ? (
            <div className={styles.centered}>
              <Spin indicator={<LoadingOutlined />} />
            </div>
          ) : sessions.length === 0 ? (
            <div className={styles.noSessions}>
              <Text className={styles.noSessionsText}>No conversations yet</Text>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className={`${styles.sessionItem} ${
                  activeSession?.id === session.id ? styles.sessionActive : ""
                }`}
                onClick={() => handleSelectSession(session.id)}
              >
                <div className={styles.sessionContent}>
                  <div className={styles.sessionTitleRow}>
                    <span className={styles.sessionTitle}>
                      {session.title || "New conversation"}
                    </span>
                  </div>
                  <span className={styles.sessionDate}>{formatDate(session.created_at)}</span>
                </div>
                {session.status === "active" ? (
                  <Popconfirm
                    title="Close this session?"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleCloseSession(session.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    <CloseOutlined
                      className={styles.sessionClose}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                ) : (
                  <Tag className={styles.sessionClosedTag}>Ended</Tag>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ---- Main Chat ---- */}
      <div className={styles.main}>
        {activeSession ? (
          <>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <RobotOutlined className={styles.headerIcon} />
                <div>
                  <div className={styles.headerTitle}>
                    {activeSession.title || "New conversation"}
                  </div>
                  <div className={styles.headerMeta}>
                    {activeSession.room_context?.room_type && (
                      <>
                        {activeSession.room_context.room_type}
                        {activeSession.room_context.area_sqft &&
                          ` · ${activeSession.room_context.area_sqft} sqft`}
                      </>
                    )}
                    {activeSession.contact_info && (
                      <span className={styles.headerContact}>
                        {activeSession.room_context?.room_type && " · "}
                        {activeSession.contact_info.name || activeSession.contact_info.phone}
                        {activeSession.contact_info.name && activeSession.contact_info.phone &&
                          ` (${activeSession.contact_info.phone})`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Tooltip title="Run this chat exactly like the WhatsApp bot: off-hours qualification, existing-customer hand-off, and auto-close after the pitch.">
                  <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748b" }}>
                    <Switch size="small" checked={simulateWhatsapp} onChange={setSimulateWhatsapp} />
                    Simulate WhatsApp
                  </span>
                </Tooltip>
                <Tooltip title={simulateWhatsapp ? "Ignore your account's order history and treat this chat as a brand-new lead, so the full qualification flow runs." : "Turn on Simulate WhatsApp to use this."}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: simulateWhatsapp ? "#64748b" : "#cbd5e1" }}>
                    <Switch size="small" checked={forceNewLead} onChange={setForceNewLead} disabled={!simulateWhatsapp} />
                    Treat as new lead
                  </span>
                </Tooltip>
                <Popconfirm
                  title="Delete your Kylas lead?"
                  description="Removes the Kylas lead for your contact so the next test starts fresh."
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={handleDeleteKylasLead}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} loading={deletingLead} style={{ fontSize: 12 }}>
                    Delete my Kylas lead
                  </Button>
                </Popconfirm>
                {isClosed && <Tag color="default">Session Ended</Tag>}
              </div>
            </div>

            {/* Messages */}
            <div className={styles.messagesWrap}>
              <div className={styles.messagesInner}>
                {!hasMessages && (
                  <div className={styles.welcome}>
                    <div className={styles.welcomeIcon}>
                      <RobotOutlined />
                    </div>
                    <h2 className={styles.welcomeTitle}>What are you designing today?</h2>
                    <p className={styles.welcomeSub}>
                      Describe your room, area, budget, and style — I'll recommend the best products.
                    </p>
                    <div className={styles.suggestionsGrid}>
                      {SUGGESTIONS.map((s, i) => (
                        <div
                          key={i}
                          className={styles.suggestionCard}
                          onClick={() => handleSendMessage(s.text)}
                        >
                          <span className={styles.suggestionIcon}>{s.icon}</span>
                          <div>
                            <div className={styles.suggestionTitle}>{s.title}</div>
                            <div className={styles.suggestionText}>{s.text}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeSession.messages
                  ?.filter((m) => m.role !== "system")
                  .map((msg) => {
                    const products: Product[] =
                      msg.products ??
                      ((msg.role === "tool" &&
                        (msg.tool_name === "search_products" ||
                          msg.tool_name === "get_inspiration_products"))
                        ? msg.tool_result?.products ?? []
                        : []);
                    if (msg.role === "tool" && products.length === 0) return null;
                    return (
                    <div
                      key={msg.id}
                      className={`${styles.messageRow} ${
                        msg.role === "user" ? styles.messageUser : styles.messageAssistant
                      }`}
                    >
                      <div className={styles.messageAvatar}>
                        {msg.role === "user" ? (
                          <UserOutlined />
                        ) : (
                          <RobotOutlined />
                        )}
                      </div>
                      <div className={styles.messageBody}>
                        <div className={styles.messageSender}>
                          {msg.role === "user" ? "You" : "Design Agent"}
                          <span className={styles.messageTime}>{formatTime(msg.created_at)}</span>
                        </div>
                        {msg.content?.trim() && (
                          <div className={styles.messageText}>
                            {msg.role === "assistant" ? formatContent(msg.content) : msg.content}
                          </div>
                        )}
                        {products.length > 0 && (
                          <div className={styles.productsGrid}>
                            {products.map((product) => (
                              <div key={product.variant_id} className={styles.productCard}>
                                <a
                                  href={`https://materialdepot.com/${product.variant_handle}/product`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.productImageWrap}
                                >
                                  <img
                                    src={getImageUrl(product.image_url, "100")}
                                    alt={product.product_name}
                                    className={styles.productImage}
                                  />
                                </a>
                                <div className={styles.productInfo}>
                                  <a
                                    href={`https://materialdepot.com/${product.variant_handle}/product`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.productName}
                                    title={product.product_name}
                                  >
                                    {product.product_name}
                                  </a>
                                  <div className={styles.productMeta}>
                                    <span className={styles.productBrand}>{product.brand}</span>
                                    <span className={styles.productSize}>{product.size}</span>
                                  </div>
                                  <div className={styles.productTags}>
                                    {product.finish?.map((f) => (
                                      <Tag key={f} className={styles.productTag}>{f}</Tag>
                                    ))}
                                    {product.material?.map((m) => (
                                      <Tag key={m} className={styles.productTag}>{m}</Tag>
                                    ))}
                                  </div>
                                  <div className={styles.productPricing}>
                                    <span className={styles.productPrice}>
                                      Rs {product.price_per_unit?.toLocaleString("en-IN")}/{product.price_unit}
                                    </span>
                                    <span className={styles.productMrp}>
                                      MRP: Rs {product.mrp?.toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}

                {sendingMessage && (
                  <div className={`${styles.messageRow} ${styles.messageAssistant}`}>
                    <div className={styles.messageAvatar}>
                      <RobotOutlined />
                    </div>
                    <div className={styles.messageBody}>
                      <div className={styles.messageSender}>Design Agent</div>
                      <div className={styles.typingDots}>
                        <span />
                        <span />
                        <span />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input */}
            <div className={styles.inputWrap}>
              <div className={styles.inputBar}>
                <TextArea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    isClosed
                      ? "This session has ended — start a new one"
                      : "Ask about tiles, laminates, sanitaryware, wallpaper..."
                  }
                  disabled={isClosed || sendingMessage}
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  className={styles.inputField}
                />
                <Button
                  type="primary"
                  shape="circle"
                  icon={<SendOutlined />}
                  onClick={() => handleSendMessage()}
                  disabled={!inputValue.trim() || isClosed || sendingMessage}
                  className={styles.sendBtn}
                />
              </div>
              <div className={styles.inputHint}>
                Enter to send · Shift+Enter for new line
              </div>
            </div>
          </>
        ) : (
          /* No session selected */
          <div className={styles.landing}>
            <div className={styles.landingContent}>
              <div className={styles.landingIcon}>
                <RobotOutlined />
              </div>
              <h1 className={styles.landingTitle}>Design Agent</h1>
              <p className={styles.landingSub}>
                AI-powered interior design recommendations.<br />
                Get curated products, cost breakdowns, and budget plans for any room.
              </p>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
                loading={creatingSession}
                className={styles.landingBtn}
              >
                Start a Conversation
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ---- Create Session Modal ---- */}
      <Modal
        title="Start New Conversation"
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        footer={null}
        centered
        destroyOnClose
        width={560}
      >
        <div className={styles.modeSection}>
          <div className={styles.modeCardDesc}>
            Start a new presales conversation for design recommendations.
          </div>
        </div>

        <Button
          type="primary"
          block
          size="large"
          onClick={handleConfirmCreateSession}
          loading={creatingSession}
          className={styles.modalCreateBtn}
        >
          Start Conversation
        </Button>
      </Modal>
    </div>
  );
};

export default DesignAgent;
